import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Profile } from "@/types";
import { createPersistStorage } from "@/lib/storage";
import { getProfiles, getProfile, updateProfile as updateProfileApi } from "@/lib/api/profiles-client";
import { useAuthStore } from "./auth-store";
import { usePreferredLanguageStore } from "./preferred-language-store";

/** Consider profiles stale after this many ms - refetch when older */
export const OTHER_PROFILES_STALE_MS = 5 * 60 * 1000; // 5 minutes

/** Min ms between profile fetches - avoids hitting API rate limit (60/min) when multiple callers fire */
const FETCH_PROFILES_COOLDOWN_MS = 45 * 1000; // 45 seconds

let fetchProfilesInFlight: Promise<void> | null = null;

interface ProfileState {
  currentProfile: Profile | null;
  otherProfiles: Profile[];
  /** Timestamp when otherProfiles was last fetched - used for stale-while-revalidate */
  lastOtherProfilesFetchedAt: number | null;
  savedProfileIds: string[];
  hydrated: boolean;
  loading: boolean;
  error: string | null;
  setCurrentProfile: (profile: Profile) => void;
  updateCurrentProfile: (updates: Partial<Profile>) => Promise<void>;
  setProfiles: (profiles: Profile[]) => void;
  fetchProfiles: (limit?: number, options?: { excludeSyncRelationships?: boolean }) => Promise<void>;
  fetchCurrentProfile: (userId: string, forceRefresh?: boolean) => Promise<void>;
  toggleSavedProfile: (profileId: string) => void;
  isProfileSaved: (profileId: string) => boolean;
  getProfileById: (profileId: string) => Profile | undefined;
  /** Merge profiles into otherProfiles (e.g. after batch match-partner fetch) without wiping the list */
  mergeIntoOtherProfiles: (profiles: Profile[]) => void;
  incrementMatchesCount: (amount?: number) => void;
  incrementEventsAttendedCount: (amount?: number) => void;
  incrementCommunityPostsCount: (amount?: number) => void;
}

// REMOVED: Fallback profile - we no longer create/use fake user profiles
// If profile fetch fails, we show an error and force re-authentication

export const useProfileStore = create<ProfileState>()(
  persist(
    (set, get) => ({
      currentProfile: null,
      otherProfiles: [],
      lastOtherProfilesFetchedAt: null,
      savedProfileIds: [],
      hydrated: false,
      loading: false,
      error: null,
      setCurrentProfile: (profile) => {
        // Handle null profile (e.g., on logout)
        if (profile === null) {
          set({ currentProfile: null });
          return;
        }
        
        // CRITICAL: Never overwrite admin with user or fallback profile
        // If profile is the fallback (temp) or has "user" role, preserve existing admin status
        const state = get();
        const existingProfile = state.currentProfile;
        
        // CRITICAL: Reject any profile with temp IDs - these are invalid
        if (profile.id === "temp" || profile.userId === "temp") {
          console.error("[Profile Store] Rejected attempt to set fallback/temp profile");
          return; // Do not set fallback profiles
        }
        
        // If existing profile is admin and new profile is user, preserve admin
        const shouldPreserveAdmin = existingProfile && 
          (existingProfile.accountType === "admin" || existingProfile.user_role === "admin") &&
          (profile.accountType === "user" || profile.user_role === "user" || !profile.accountType);
        
        const finalProfile = shouldPreserveAdmin ? {
          ...profile,
          accountType: existingProfile.accountType || "admin",
          user_role: existingProfile.user_role || "admin",
        } : profile;
        
        set({
          currentProfile: {
            ...finalProfile,
            updatedAt: new Date().toISOString(),
          },
          otherProfiles: [
            ...state.otherProfiles.filter((item) => item.id !== finalProfile.id),
            state.currentProfile && state.currentProfile.id !== finalProfile.id ? state.currentProfile : undefined,
          ].filter(Boolean) as Profile[],
        });
      },
      updateCurrentProfile: async (updates) => {
        const state = get();
        if (!state.currentProfile) return;

        // Get the auth user's ID instead of using profile.userId which might be "temp"
        const authUser = useAuthStore.getState().user;
        const userId = authUser?.id || state.currentProfile.userId;
        
        // Validate that we have a valid userId
        if (!userId || userId === "temp" || userId.trim() === "") {
          console.error("Cannot update profile: No valid user ID available");
          set({ error: "Cannot update profile: Please log in again", loading: false });
          return;
        }

        // Optimistic update: apply changes to the store immediately before the API
        // call completes. This prevents a Capacitor app-resume fetchCurrentProfile
        // (triggered within ~100ms via onRehydrateStorage) from reinstating stale
        // cached data and reverting the UI (e.g. the newly chosen profile photo)
        // while the PUT request is still in flight.
        const prevProfile = state.currentProfile;
        set({
          loading: true,
          error: null,
          currentProfile: {
            ...prevProfile,
            ...(updates as Partial<Profile>),
            updatedAt: new Date().toISOString(),
          },
        });

        try {
          const updated = await updateProfileApi(userId, updates);
          set((prevState) => ({
            currentProfile: updated,
            otherProfiles: prevState.otherProfiles.map((profile) =>
              profile.id === updated.id ? updated : profile,
            ),
            loading: false,
          }));
        } catch (error: any) {
          console.error("Failed to update profile:", error);
          const message = error?.message || "Failed to update profile";
          // Roll back the optimistic update so the UI reverts to the last confirmed state
          set({ currentProfile: prevProfile, error: message, loading: false });
          throw error;
        }
      },
      fetchProfiles: async (limit?: number, options?: { excludeSyncRelationships?: boolean }) => {
        const state = get();
        const lastFetched = state.lastOtherProfilesFetchedAt;
        const now = Date.now();
        const resolvedOptions = {
          // Safe-by-default for discover/dashboard candidate lists.
          // Callers can explicitly pass false if they need unfiltered profiles.
          excludeSyncRelationships: options?.excludeSyncRelationships ?? true,
        };

        // Skip if we fetched recently - avoids hitting rate limit (60/min) when boot, preload, dashboard, discover all fire
        if (lastFetched && now - lastFetched < FETCH_PROFILES_COOLDOWN_MS) {
          if (process.env.NODE_ENV === "development") {
            console.log("📦 [Profile Store] Skipping fetchProfiles (cooldown)", {
              lastFetchedAgo: Math.round((now - lastFetched) / 1000) + "s",
              cooldownMs: FETCH_PROFILES_COOLDOWN_MS,
            });
          }
          set({ loading: false });
          return;
        }

        // In-flight deduplication - return existing promise if already fetching
        if (fetchProfilesInFlight) {
          await fetchProfilesInFlight;
          set({ loading: false });
          return;
        }

        const doFetch = async () => {
          set({ loading: true, error: null });
          const isDev = process.env.NODE_ENV === "development";
          const safeLimit = Math.min(Math.max(limit ?? 120, 20), 200);

          try {
            if (isDev) {
              console.log("[Profile Store] [DEV] Starting fetchProfiles...");
            }

            const profiles = await getProfiles(safeLimit, resolvedOptions);
            const rawProfilesCount = profiles.length;

            if (isDev) {
              console.log("[Profile Store] [DEV] getProfiles returned", {
                rawProfilesCount,
                profileIds: profiles.slice(0, 10).map(p => ({
                  id: p.id,
                  userId: p.userId,
                  fullName: p.fullName,
                })),
              });
            }

            // The /api/profiles endpoint already excludes the current user (it's a discovery
            // endpoint), so every profile returned is an "other". currentProfile is managed
            // separately via fetchCurrentProfile and must not be set here.
            set({
              otherProfiles: profiles,
              lastOtherProfilesFetchedAt: Date.now(),
              loading: false,
            });
        } catch (error: any) {
          console.error("[Profile Store] Failed to fetch profiles:", error);
          const errorMessage = error?.message || "Failed to fetch profiles";
          // On rate limit (429), preserve existing data so UI doesn't go blank
          const isRateLimit = errorMessage.includes("Too many requests");
          set({
            error: errorMessage,
            loading: false,
            ...(isRateLimit ? {} : { otherProfiles: [], lastOtherProfilesFetchedAt: null }),
          });
        } finally {
          fetchProfilesInFlight = null;
        }
        };

        fetchProfilesInFlight = doFetch();
        await fetchProfilesInFlight;
      },
      fetchCurrentProfile: async (userId: string, forceRefresh: boolean = false) => {
        set({ loading: true, error: null });
        try {
          // CRITICAL: Validate userId is a valid Supabase auth user ID
          if (!userId || userId === "temp" || userId.trim() === "") {
            console.error("[Profile Store] Invalid userId provided to fetchCurrentProfile:", userId);
            set({ error: "Invalid user ID", loading: false });
            return;
          }
          
          // Preserve existing account type if we have one (only if not forcing refresh)
          const existingProfile = get().currentProfile;
          const existingAccountType = !forceRefresh ? existingProfile?.accountType : undefined;
          const existingUserRole = !forceRefresh ? existingProfile?.user_role : undefined;
          
          const profile = await getProfile(userId, { forceRefresh });
          
          if (profile) {
            // CRITICAL: Verify the profile belongs to this auth user
            if (profile.userId !== userId) {
              console.error(
                "[Profile Store] CRITICAL MISMATCH: Profile does not belong to requested userId!",
                {
                  requestedUserId: userId,
                  profileUserId: profile.userId,
                  profileId: profile.id,
                }
              );
              set({ error: "Profile mismatch", loading: false });
              return;
            }
            
            // Check if API returned admin status
            const apiSaysAdmin = profile.accountType === "admin" || profile.user_role === "admin";
            
            // CRITICAL: Always trust API response for admin status
            // However, if API returns null/undefined for accountType/user_role AND we have existing admin status,
            // preserve it (API might have temporarily failed or returned incomplete data)
            // This prevents clearing admin status due to transient API issues
            const shouldPreserveAdmin = 
              !forceRefresh &&
              !apiSaysAdmin &&
              (existingAccountType === "admin" || existingUserRole === "admin") &&
              (!profile.accountType && !profile.user_role); // Only preserve if API returned null for both
            
            // CRITICAL: If API says admin, always use that (even if existing profile says different)
            // This ensures admin status is always up-to-date from server
            
            // Determine final account type and role
            const finalAccountType = apiSaysAdmin 
              ? "admin" 
              : (shouldPreserveAdmin ? existingAccountType : (profile.accountType || null));
            const finalUserRole = apiSaysAdmin
              ? "admin"
              : (shouldPreserveAdmin ? existingUserRole : (profile.user_role || null));
            
            // CRITICAL: If admin, ensure membership tier is Elite (even if API says different)
            const isAdmin = finalAccountType === "admin" || finalUserRole === "admin";
            const finalMembershipTier = isAdmin ? "elite" : (profile.membershipTier || "free");
            
            const finalProfile = {
              ...profile,
              accountType: finalAccountType,
              user_role: finalUserRole,
              membershipTier: finalMembershipTier,
            };
            
            set({ currentProfile: finalProfile, loading: false });
          } else {
            console.warn("[Profile Store] No profile found for userId:", userId);
            console.warn("[Profile Store] Profile fetch returned null - this could mean:", {
              userId,
              possibleReasons: [
                "Profile doesn't exist in database",
                "Profile API returned 404",
                "Profile API authentication failed (401) - auth may still be resolving",
                "Network error",
                "RLS policy blocking query",
              ],
            });
            // CRITICAL: Set error so boot sequence knows to stop retrying after max attempts
            // This prevents infinite retry loops when profile truly doesn't exist or RLS is blocking
            // Boot sequence will handle retries with proper timeout, then require re-login
            set({ 
              currentProfile: null, 
              error: "Profile not found. Please log in again.", // Set error so boot sequence can handle it
              loading: false 
            });
          }
        } catch (error: any) {
          const authUser = useAuthStore.getState().user;
          const message = error?.message || "";
          const isTimeout = 
            message.includes("timed out") ||
            message.includes("timeout") ||
            error?.name === "AbortError";
          const isDbOffline =
            message.includes("Can't reach database server") ||
            message.includes("ECONNREFUSED") ||
            message.includes("ENOTFOUND") ||
            message.includes("503") ||
            message.includes("temporarily unavailable");
          const isNetworkTransient =
            message.includes("fetch") ||
            message.includes("network") ||
            message.includes("ECONNRESET") ||
            message.includes("Failed to fetch");

          console.error("[Profile Store] Failed to fetch profile:", error);
          console.error("[Profile Store] Error details:", {
            message: error?.message,
            stack: error?.stack,
            isTimeout,
            isDbOffline,
            isNetworkTransient,
            userId,
          });
          
          // CRITICAL: Handle timeout errors gracefully - don't set error, allow retry
          // Timeouts are often transient network issues
          if (isTimeout) {
            console.warn("[Profile Store] Profile fetch timed out - will retry on next attempt");
            set({ 
              currentProfile: null, 
              error: null, // Don't set error for timeouts - allow retry
              loading: false 
            });
            return;
          }
          
          // CRITICAL: On transient db/network errors, preserve existing profile if it matches userId.
          // Avoids "something went wrong" in installed app when network is briefly unavailable.
          const existingProfile = get().currentProfile;
          const hasMatchingCachedProfile = existingProfile && existingProfile.userId === userId;
          if ((isDbOffline || isNetworkTransient) && hasMatchingCachedProfile) {
            console.warn("[Profile Store] Transient error but have cached profile - preserving, no hard error");
            set({ error: null, loading: false });
            return;
          }
          
          // CRITICAL: Clear profile but DO NOT logout - let boot sequence handle re-auth
          // Boot sequence will retry and show blocking error if needed
          // DO NOT create fallback profiles - user must have valid profile to proceed
          set({ 
            currentProfile: null, 
            error: error?.message || "Failed to fetch profile. Please log in again.", 
            loading: false 
          });
        }
      },
      setProfiles: (profiles) =>
        set((state) => ({
          otherProfiles: profiles.filter((profile) => 
            state.currentProfile ? profile.id !== state.currentProfile.id : true
          ),
        })),
      toggleSavedProfile: (profileId) =>
        set((state) => {
          const isSaved = state.savedProfileIds.includes(profileId);
          return {
            savedProfileIds: isSaved
              ? state.savedProfileIds.filter((id) => id !== profileId)
              : [...state.savedProfileIds, profileId],
          };
        }),
      isProfileSaved: (profileId) => get().savedProfileIds.includes(profileId),
      getProfileById: (profileIdOrUserId) => {
        const { currentProfile, otherProfiles } = get();
        if (!profileIdOrUserId) return undefined;
        // Match by profile.id (table PK) or profile.userId (auth user id) so lookups by either work
        if (currentProfile && (currentProfile.id === profileIdOrUserId || currentProfile.userId === profileIdOrUserId)) {
          return currentProfile;
        }
        return otherProfiles.find(
          (profile) => profile.id === profileIdOrUserId || profile.userId === profileIdOrUserId
        );
      },
      mergeIntoOtherProfiles: (profiles) => {
        if (!profiles.length) return;
        set((state) => {
          const byId = new Map(state.otherProfiles.map((p) => [p.id, p]));
          for (const p of profiles) {
            if (state.currentProfile?.id === p.id) continue;
            byId.set(p.id, p);
          }
          return { otherProfiles: Array.from(byId.values()) };
        });
      },
      incrementMatchesCount: (amount = 1) =>
        set((state) => {
          if (!state.currentProfile) return state;
          const updated = {
            ...state.currentProfile,
            matchesCount: state.currentProfile.matchesCount + amount,
            updatedAt: new Date().toISOString(),
          };

          return {
            currentProfile: updated,
            otherProfiles: state.otherProfiles.map((profile) =>
              profile.id === updated.id ? updated : profile,
            ),
          };
        }),
      incrementEventsAttendedCount: (amount = 1) =>
        set((state) => {
          if (!state.currentProfile) return state;
          const updated = {
            ...state.currentProfile,
            eventsAttendedCount: state.currentProfile.eventsAttendedCount + amount,
            updatedAt: new Date().toISOString(),
          };

          return {
            currentProfile: updated,
            otherProfiles: state.otherProfiles.map((profile) =>
              profile.id === updated.id ? updated : profile,
            ),
          };
        }),
      incrementCommunityPostsCount: (amount = 1) =>
        set((state) => {
          if (!state.currentProfile) return state;
          const updated = {
            ...state.currentProfile,
            communityPostsCount: state.currentProfile.communityPostsCount + amount,
            updatedAt: new Date().toISOString(),
          };

          return {
            currentProfile: updated,
            otherProfiles: state.otherProfiles.map((profile) =>
              profile.id === updated.id ? updated : profile,
            ),
          };
        }),
    }),
    {
      name: "growthsync-profile",
      storage: createPersistStorage(),
      onRehydrateStorage: () => (state) => {
        // Always mark hydrated when rehydration completes (even with empty storage)
        useProfileStore.setState({ hydrated: true });
        if (state) {
          state.hydrated = true;

          // CRITICAL: Only clear profile when auth store has rehydrated and user id doesn't match.
          // On mobile, auth can rehydrate after profile - don't clear profile just because auth isn't ready yet.
          const authState = useAuthStore.getState();
          const authUser = authState.user;
          if (
            authState._hasHydrated &&
            authUser?.id &&
            state.currentProfile?.userId &&
            state.currentProfile.userId !== authUser.id
          ) {
            state.currentProfile = null;
            useProfileStore.setState({ currentProfile: null });
          }

          // Preserve account type from rehydrated state
          const preservedAccountType = state.currentProfile?.accountType;
          const preservedUserRole = state.currentProfile?.user_role;

          // preservedAccountType and preservedUserRole maintained for account type sync below

          // Always fetch fresh data from API after rehydration to ensure we have latest from database
          if (typeof window !== "undefined") {
            // Use setTimeout to ensure this runs after hydration is complete
            setTimeout(() => {
              const authUser = useAuthStore.getState().user;
              if (authUser && authUser.id && authUser.id !== "temp") {
                // Always use auth.user.id (Supabase Auth user ID) - never use profileId or email
                const userId = authUser.id;
                // Always fetch to get latest data from database, even if we have cached data
                // Use forceRefresh=false to preserve admin status if API temporarily returns wrong value
                state.fetchCurrentProfile(userId, false).then(() => {
                  // Profile refreshed after rehydration - account type and role preserved
                }).catch((err) => {
                  console.error("[Profile Store] Error fetching profile after rehydration:", err);
                });
              } else {
                // If no valid auth user, don't try to fetch profiles
                console.warn("[Profile Store] Cannot fetch profile: No valid auth user ID");
              }
            }, 100); // Small delay to ensure hydration is complete
          }
        }
      },
    },
  ),
);

// CRITICAL: Fallback so hydrated becomes true even if onRehydrateStorage never fires.
// profile-store mirrors the same pattern used by auth-store and match-store.
// On iOS WebView/Capacitor and some Android WebViews the persist callback can be
// delayed or silently dropped, leaving hydrated=false forever and the Sync Requests
// tab permanently stuck on a spinner.
if (typeof window !== "undefined") {
  const isSafari = /Safari/i.test(navigator.userAgent) && !/Chrome/i.test(navigator.userAgent);
  const isAndroidApp = /Android/i.test(navigator.userAgent) && (window as any).Capacitor?.getPlatform?.() === "android";
  const HYDRATION_FALLBACK_MS = (isSafari || isAndroidApp) ? 450 : 150;
  setTimeout(() => {
    if (!useProfileStore.getState().hydrated) {
      useProfileStore.setState({ hydrated: true });
    }
  }, HYDRATION_FALLBACK_MS);
}

/* Mirror the one field I18nProvider needs into a store that imports nothing.
 *
 * One subscription rather than a line at each of the fourteen places currentProfile is
 * assigned, because thirteen of those would eventually be updated and one would not.
 * See stores/preferred-language-store.ts for why the split exists at all. */
useProfileStore.subscribe((state, prev) => {
  const next = state.currentProfile?.preferred_language ?? null;
  const before = prev.currentProfile?.preferred_language ?? null;
  if (next !== before) {
    usePreferredLanguageStore.getState().setPreferredLanguage(next);
  }
});
